import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import './styles/learningHome.scss'
import { SECTION_TILES, useSections } from '../../sections';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';
import ProgressCircle from '../../components/ProgressCircle';
import SectionIcon from '../../components/SectionIcon';
import { formatPrice } from '../../components/ProductCatalog';

type Goal = { id: string; name: string; examDate: string | null };
type UpcomingPayment = { id: string; product: number; amount: number; dueDate: string };

type SubjectInfo = {
    key: string;
    title: string;
    img: string;
    route: string;
    progress: number;
    ready: boolean;
};

// Locative forms so the hero reads grammatically ("Pokračuj ve Fyzice", not "v Fyzika").
const SUBJECT_LOCATIVE: Record<string, string> = { fyzika: "ve Fyzice", chemie: "v Chemii", biologie: "v Biologii" };

function formatDate(d: string) {
    const date = new Date(d);
    return date.getDate() + ". " + (date.getMonth() + 1) + ". " + date.getFullYear();
}

function daysUntil(d: string) {
    return Math.ceil((new Date(d).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function LearningHome() {
    const navigate = useNavigate();
    const { hasSection, loading: sectionsLoading } = useSections();

    const [goal, setGoal] = useState<Goal | null>(null);
    const [progress, setProgress] = useState({ physicsProgress: 0, biologyProgress: 0, chemistryProgress: 0 });
    const [payment, setPayment] = useState<UpcomingPayment | null>(null);
    const [streak, setStreak] = useState(0);
    const [loading, setLoading] = useState(true);

    const api = new ApiHelper();

    async function fetchData() {
        try {
            const [progressRes, goalRes] = await Promise.all([
                api.lessonsApi.getProgressList(),
                api.userApi.getGoalList(),
            ]);

            setProgress(await readJson(progressRes));
            setGoal(await readJson(goalRes));

            try {
                const streakRes = await api.dashboardApi.streakList();
                setStreak((await readJson(streakRes)).currentStreak);
            } catch { /* streak is non-critical */ }

            if (hasSection("Account")) {
                const payRes = await api.ordersApi.getUpcomingPaymentsList();
                const payments: UpcomingPayment[] = await readJson(payRes);
                setPayment(payments.length > 0 ? payments[0] : null);
            }
        } catch (x: any) {
            api.processErrors(x);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!sectionsLoading) fetchData();
    }, [sectionsLoading]);

    const subjects: SubjectInfo[] = [
        { key: "fyzika", title: "Fyzika", img: "/images/components/atom.png", route: "/kurz/materialy/fyzika", progress: progress.physicsProgress, ready: true },
        { key: "chemie", title: "Chemie", img: "/images/components/isopropanol.png", route: "/kurz/materialy/chemie", progress: progress.chemistryProgress, ready: true },
        { key: "biologie", title: "Biologie", img: "/images/components/cell.png", route: "/kurz/materialy/biologie", progress: progress.biologyProgress, ready: true },
    ];

    const hasMaterials = hasSection("StudyMaterials");
    const recommended = subjects
        .filter(s => s.ready && s.progress < 100)
        .sort((a, b) => a.progress - b.progress)[0];

    const examDays = goal?.examDate ? daysUntil(goal.examDate) : null;

    const tiles = SECTION_TILES.filter(t => hasSection(t.section));

    const milestones = subjects
        .filter(s => s.ready && s.progress > 0)
        .map(s => {
            if (s.progress >= 100) return { label: s.title + " dokončena", tone: "gold" };
            if (s.progress >= 50) return { label: s.title + " z poloviny", tone: "silver" };
            return { label: "Rozjezd: " + s.title, tone: "bronze" };
        });

    function heroAction() {
        if (!goal) {
            return { eyebrow: "Začínáme", title: "Nastav si cíl", text: "Vyber cílovou fakultu a spustíme odpočet k přijímačkám.", cta: "Nastavit cíl", onClick: () => navigate("/kurz/ucet") };
        }
        if (hasMaterials && recommended) {
            const where = SUBJECT_LOCATIVE[recommended.key] ?? ("v předmětu " + recommended.title);
            return { eyebrow: "Co dál", title: "Pokračuj " + where, text: "Máš hotovo " + recommended.progress + " %. Naskoč zpět do studia tam, kde jsi skončil.", cta: "Pokračovat ve studiu", onClick: () => navigate(recommended.route) };
        }
        if (hasSection("OnlineLessons")) {
            return { eyebrow: "Co dál", title: "Tvoje online lekce", text: "Podívej se na rozvrh lekcí a připoj se k té nejbližší.", cta: "Otevřít online lekce", onClick: () => navigate("/kurz/vyuka") };
        }
        return { eyebrow: "Hotovo", title: "Máš vše nastudované!", text: "Skvělá práce. Otestuj se v procvičování nebo si zopakuj materiály.", cta: "Zobrazit materiály", onClick: () => navigate("/kurz/materialy") };
    }

    const hero = heroAction();

    if (loading || sectionsLoading) {
        return (
            <div className="dashboard">
                <div className="skeleton hero" />
                <div className="skeletonRow">
                    <div className="skeleton" /><div className="skeleton" /><div className="skeleton" />
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <section className={"hero" + (!goal ? " setup" : "")}>
                <div className="heroMain">
                    <span className="eyebrow">{hero.eyebrow}</span>
                    <h1>{hero.title}</h1>
                    <p>{hero.text}</p>
                    <div className="heroCta" onClick={hero.onClick}>{hero.cta}</div>
                </div>

                {goal &&
                    <div className="heroGoal">
                        {examDays !== null && examDays >= 0 ?
                            <>
                                <span className="count">{examDays}</span>
                                <span className="unit">dní do přijímaček</span>
                            </>
                            :
                            <span className="unit">cílová fakulta</span>
                        }
                        <span className="faculty">{goal.name}</span>
                        <span className="examDate">{goal.examDate ? formatDate(goal.examDate) : "Datum nebylo zatím zveřejněno"}</span>
                    </div>
                }
            </section>

            {payment && (() => {
                const days = daysUntil(payment.dueDate);
                if (days > 14) return null;
                const overdue = days < 0;
                const label = overdue
                    ? "Platba po splatnosti"
                    : days === 0
                    ? "Platba je splatná dnes"
                    : days <= 7
                    ? "Platba splatná za " + days + (days < 5 ? " dny" : " dní")
                    : "Nadcházející platba";

                return (
                    <Link to={"/platba/" + payment.id} className={"paymentBanner" + (overdue ? " overdue" : "")}>
                        <span>{label} <b>{formatPrice(payment.amount)}</b> — splatnost {formatDate(payment.dueDate)}</span>
                        <span className="pay">Zaplatit →</span>
                    </Link>
                );
            })()}

            {hasMaterials &&
                <section className="block">
                    <div className="blockHead">
                        <h2 className="blockTitle">Tvůj pokrok</h2>
                        {streak > 0 &&
                            <span className="streakBadge" title="Kolik dní v řadě studuješ">
                                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M13 2c.5 3-1.5 4.5-3 6S7 11.5 7 14a5 5 0 0 0 10 0c0-2-1-3.8-2.2-5C14 8 13.5 6 13 2Z" />
                                </svg>
                                {streak} {streak === 1 ? "den" : (streak < 5 ? "dny" : "dní")} v řadě
                            </span>
                        }
                    </div>
                    <div className="subjects">
                        {subjects.map(s => (
                            <div
                                key={s.key}
                                className={"subjectCard" + (s.ready ? "" : " disabled")}
                                onClick={() => s.ready && navigate(s.route)}
                            >
                                <ProgressCircle value={s.progress} img={s.img} />
                                <h3>{s.title}</h3>
                                <span className="pct">{s.ready ? s.progress + " % hotovo" : "Připravujeme"}</span>
                            </div>
                        ))}
                    </div>
                </section>
            }

            <div className="twoCol">
                {hasMaterials &&
                    <section className="block tempo">
                        <h2 className="blockTitle">Co dnes</h2>
                        {recommended ?
                            <>
                                <p>Pokračuj v předmětu <b>{recommended.title}</b> — máš hotovo {recommended.progress} %.</p>
                                <div className="smallCta" onClick={() => navigate(recommended.route)}>Pustit se do toho</div>
                            </>
                            :
                            <p>Máš hotové všechny dostupné předměty. Procvičuj a opakuj!</p>
                        }
                    </section>
                }

                <section className="block milestones">
                    <h2 className="blockTitle">Odznaky a milníky</h2>
                    {milestones.length > 0 ?
                        <div className="badges">
                            {milestones.map((m, i) => (
                                <div key={i} className={"badge " + m.tone}>{m.label}</div>
                            ))}
                        </div>
                        :
                        <p className="empty">Zatím žádné odznaky — začni studovat a sbírej je.</p>
                    }
                </section>
            </div>

            <section className="block">
                <h2 className="blockTitle">Kam dál</h2>
                <div className={"tiles" + (tiles.length > 4 ? " balanced" : "")}>
                    {tiles.map(t => (
                        <div
                            key={t.section}
                            className={"tile" + (t.ready ? "" : " soon")}
                            onClick={() => t.ready && navigate(t.route)}
                        >
                            <div className="icon"><SectionIcon section={t.section} /></div>
                            <div className="tileText">
                                <h3>{t.title}</h3>
                                <span>{t.ready ? t.subtitle : "Připravujeme"}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
